import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { useAirModal } from "@air/provider-modal";
import Router from "next/router";
import { memo, useCallback } from "react";
import { useCanAnnotateCentralizedPublicClip } from "~/components/Annotations/hooks/useCanAnnotateCentralizedPublicClip";
import { useAnnotationV2Context } from "~/components/AnnotationsV2/providers/AnnotationV2Provider";
import { InitialCommentInput } from "~/components/DiscussionsV2/InitialCommentInput/InitialCommentInput";
import { PublicCommentAnonymousSignUpModal } from "~/components/PublicLinkAnonymousSignUpModal/PublicCommentAnonymousSignUpModal";
import { usePublicWorkspace } from "~/components/PublicWorkspaceProvider";
import { useURLBoardIdSelector } from "~/hooks/useURLBoardIdSelector";
import { useAccountContext } from "~/providers/AccountProvider";
import { centralizedBoardAncestorsSelector } from "~/store/centralizedBoard/selectors";
import { activeTimestampParamSelector } from "~/store/router/selectors";
import { useGetDiscussionMentionsMembers } from "~/swr-hooks/discussions/useGetDiscussionMentionsMembers";
import { useSubmitNewDiscussion } from "~/swr-hooks/discussions/useSubmitNewDiscussion";
import { useAirSelector, useAirStore } from "~/utils/ReduxUtils";
export const PublicDiscussionsForm = /*#__PURE__*/ memo(()=>{
    const { data: account } = useAccountContext();
    const { annotations, setAnnotations } = useAnnotationV2Context();
    const { canSeeClipAnnotation } = useCanAnnotateCentralizedPublicClip();
    const store = useAirStore();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { submitNewDiscussion } = useSubmitNewDiscussion();
    const [showSignUpModal] = useAirModal(PublicCommentAnonymousSignUpModal);
    const boardId = useURLBoardIdSelector();
    const { workspaceId: clipWorkspaceId } = usePublicWorkspace();
    const ancestors = useAirSelector(centralizedBoardAncestorsSelector);
    const mentionsList = useGetDiscussionMentionsMembers({
        boardId,
        ancestors,
        workspaceId: clipWorkspaceId
    });
    const handleNewDiscussionSubmit = useCallback((newComment, timestamp)=>{
        Router.ready(()=>{
            const submitDiscussion = ()=>{
                submitNewDiscussion({
                    trackLocation: "public-board",
                    newComment,
                    timestamp: timestamp || activeTimestampParamSelector(store.getState()),
                    annotations
                });
                // Clear annotations after submission
                setAnnotations([]);
            };
            if (account) {
                submitDiscussion();
            } else {
                const ssoCallbackData = {
                    url: Router.asPath,
                    action: {
                        action: "create-public-discussion",
                        data: {
                            newComment,
                            timestamp: timestamp || activeTimestampParamSelector(store.getState()),
                            annotations,
                            trackLocation: "public-board"
                        }
                    }
                };
                showSignUpModal({
                    onLoginSuccess: submitDiscussion,
                    ssoCallbackData
                });
            }
        });
    }, [
        account,
        annotations,
        setAnnotations,
        showSignUpModal,
        submitNewDiscussion,
        store
    ]);
    return /*#__PURE__*/ _jsx(InitialCommentInput, {
        canAnnotateClip: canSeeClipAnnotation,
        onSubmit: handleNewDiscussionSubmit,
        placeholder: "Add a comment",
        mentionsList: mentionsList,
        shouldShowMentionsButton: isAboveMediumScreen,
        hasInternalAvatar: !isAboveMediumScreen
    });
});
PublicDiscussionsForm.displayName = "PublicDiscussionsForm";
